import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${window.__BACKEND_URL__}/api`;
const money = (v) => `$${(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const marginColor = (m) => ((m ?? 0) < 0 ? '#EF4444' : (m ?? 0) === 0 ? '#A0AEC0' : '#00C896');

export default function CustomerScorecard({ customer, token, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!customer) return;
    setData(null); setError(null);
    axios.get(`${API}/customers/${customer.id}/scorecard`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load scorecard'));
  }, [customer, token]);

  const sat = data?.satisfaction;
  const trendHasData = (data?.trend || []).some((t) => t.revenue > 0 || t.cost > 0);
  return (
    <Dialog open={!!customer} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="customer-scorecard-dialog">
        <DialogHeader>
          <DialogTitle>{customer?.name} — Scorecard</DialogTitle>
          <DialogDescription>Profitability and satisfaction at a glance</DialogDescription>
        </DialogHeader>
        {!data && !error && (
          <p className="text-sm py-6 text-center flex items-center justify-center gap-2" style={{ color: '#A0AEC0' }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </p>
        )}
        {error && <p className="text-sm py-4 text-center" style={{ color: '#EF4444' }} data-testid="scorecard-error">{error}</p>}
        {data && (
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="flex flex-wrap gap-3" data-testid="scorecard-chips">
              {[
                ['Jobs', data.summary.jobs, '#A0AEC0'],
                ['Revenue', money(data.summary.revenue), '#1E88E5'],
                ['Cost', money(data.summary.cost), '#FFA000'],
                ['Margin', money(data.summary.margin), marginColor(data.summary.margin)],
                ['Margin %', data.summary.margin_pct == null ? '—' : `${data.summary.margin_pct}%`, marginColor(data.summary.margin)],
              ].map(([l, v, c]) => (
                <div key={l} className="px-3 py-1.5 rounded-lg" style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: '#64748B' }}>{l}</p>
                  <p className="text-sm font-bold" style={{ color: c }}>{v}</p>
                </div>
              ))}
            </div>

            <div className="p-3 rounded-lg" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="scorecard-satisfaction">
              <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: '#64748B' }}>Customer satisfaction</p>
              {sat?.rated > 0 ? (
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1" style={{ color: '#00C896' }}><ThumbsUp className="w-4 h-4" /> {sat.up}</span>
                  <span className="flex items-center gap-1" style={{ color: '#EF4444' }}><ThumbsDown className="w-4 h-4" /> {sat.down}</span>
                  <span className="font-bold" style={{ color: sat.pct_positive >= 70 ? '#00C896' : '#FFA000' }}>{sat.pct_positive}% positive</span>
                  <span className="text-xs" style={{ color: '#64748B' }}>({sat.rated} rated job{sat.rated !== 1 ? 's' : ''})</span>
                </div>
              ) : (
                <p className="text-xs" style={{ color: '#64748B' }}>No portal ratings from this customer yet.</p>
              )}
            </div>

            {trendHasData && (
              <div data-testid="scorecard-trend">
                <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: '#64748B' }}>Margin % — last 6 months (completed jobs)</p>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" />
                      <XAxis dataKey="month" stroke="#64748B" fontSize={10} />
                      <YAxis stroke="#64748B" fontSize={10} unit="%" />
                      <Tooltip contentStyle={{ background: '#1A2433', border: '1px solid #2D3748', borderRadius: 8, fontSize: 12 }} />
                      <Line type="monotone" dataKey="margin_pct" name="Margin %" stroke="#00C896" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
